import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import Logo from '../../../assets/logo.png';
import Address from '../../../components/Address';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import DocumentInputMasked from '../../../components/DocumentInputMasked';
import PhoneInputMasked from '../../../components/PhoneInputMasked';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
const digitsOnly = value => String(value ?? '').replace(/\D/g, '');

const STEP_FIELD_KEYS_CREATE = [
    ['name', 'businessName', 'document', 'phone', 'description'],
    ['zipcode', 'street', 'neighborhood', 'city', 'state', 'number', 'complement', 'latitude', 'longitude'],
    ['loginEmail', 'password', 'confirmPassword'],
];

const STEP_FIELD_KEYS_EXISTING = [
    ['name', 'businessName', 'document', 'phone', 'description'],
    ['zipcode', 'street', 'neighborhood', 'city', 'state', 'number', 'complement', 'latitude', 'longitude'],
    ['loginEmail', 'password'],
];

/** Campo formulário → índice do step (feedback ao enviar tudo inválido) */
const FIELD_TO_STEP = {};
STEP_FIELD_KEYS_CREATE.forEach((keys, stepIdx) => {
    keys.forEach(k => {
        FIELD_TO_STEP[k] = stepIdx;
    });
});

const baseCompanyAddressSchema = {
    name: yup.string().trim().required('Informe o nome do anunciante.'),
    businessName: yup.string().trim().required('Informe a razão social.'),
    document: yup
        .string()
        .required('Informe o documento.')
        .test('doc', 'Informe um CPF ou CNPJ válido.', v => digitsOnly(v).length >= 11),
    phone: yup.string().trim().required('Informe o telefone.'),
    description: yup.string().trim().required('Informe uma descrição sobre a loja.'),
    zipcode: yup
        .string()
        .required('Informe o CEP.')
        .test('cep', 'CEP deve ter 8 dígitos.', v => digitsOnly(v).length === 8),
    street: yup.string().trim().required('Informe o logradouro.'),
    neighborhood: yup.string().trim().required('Informe o bairro.'),
    city: yup.string().trim().required('Informe a cidade.'),
    state: yup
        .string()
        .trim()
        .required('Informe a UF.')
        .length(2, 'Informe a sigla da UF com 2 letras.'),
    number: yup.string().trim().required('Informe o número.'),
    complement: yup.string().trim().optional(),
    latitude: yup.string().trim().optional(),
    longitude: yup.string().trim().optional(),
};

const registerCreateSchema = yup.object({
    ...baseCompanyAddressSchema,
    loginEmail: yup.string().trim().required('Informe o e-mail.').email('E-mail inválido.'),
    password: yup.string().required('Informe a senha.').min(6, 'Senha deve ter no mínimo 6 caracteres.'),
    confirmPassword: yup
        .string()
        .required('Confirme a senha.')
        .oneOf([yup.ref('password')], 'As senhas não conferem.'),
});

const registerExistingSchema = yup.object({
    ...baseCompanyAddressSchema,
    loginEmail: yup.string().trim().required('Informe o e-mail.').email('E-mail inválido.'),
    password: yup.string().required('Informe a senha da sua conta.'),
});

const HERO_IMAGE =
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=2000&q=80';

const STEPS = [
    { label: 'Empresa', description: 'Identificação e contato da marca' },
    { label: 'Endereço', description: 'Onde você atende seus clientes' },
    { label: 'Acesso', description: 'E-mail e senha do painel' },
];

const ADVERTISER_LOGIN_PATH = '/anunciante/login';
const ADVERTISER_RECOVER_PATH = '/recuperar-senha?profile=advertiser';

export default function RegisterAdvertiser() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [activeStep, setActiveStep] = useState(0);
    const [accessMode, setAccessMode] = useState('create'); // 'create' | 'existing'
    const { setErrors, enqueueSnackbar } = useErrors();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const clubCodeFromUrl =
        searchParams.get('code') || searchParams.get('clubCode') || searchParams.get('accessCode') || '';
    const clubIdFromUrl = searchParams.get('clubId') || '';
    const hasClubReferralFromUrl = Boolean(clubIdFromUrl || clubCodeFromUrl);
    const [showTip, setShowTip] = useState(false);

    const isExistingAccount = accessMode === 'existing';
    const registerSchema = isExistingAccount ? registerExistingSchema : registerCreateSchema;
    const stepFieldKeys = isExistingAccount ? STEP_FIELD_KEYS_EXISTING : STEP_FIELD_KEYS_CREATE;
    const stepSchemas = useMemo(
        () => stepFieldKeys.map(keys => registerSchema.pick(keys)),
        [registerSchema, stepFieldKeys],
    );

    const { values, errors, handleChange, handleSubmit, setFieldValue, setFieldError, isSubmitting } = useFormik({
        initialValues: {
            name: '',
            businessName: '',
            document: '',
            phone: '',
            description: '',
            zipcode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            latitude: '',
            longitude: '',
            address: '',
            loginEmail: '',
            password: '',
            confirmPassword: '',
            showPassword: false,
            showConfirmPassword: false,
            clubCode: clubCodeFromUrl,
            existingAccount: false,
        },
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (formValues, { setSubmitting, setFieldError: setFbFieldError }) => {
            const useExisting = Boolean(formValues.existingAccount);
            const schema = useExisting ? registerExistingSchema : registerCreateSchema;
            try {
                await schema.validate(formValues, { abortEarly: false });
            } catch (validationError) {
                if (validationError?.inner?.length) {
                    validationError.inner.forEach(err => {
                        if (err.path) setFbFieldError(err.path, err.message);
                    });
                    const stepsWithErrors = validationError.inner
                        .map(e => (e.path ? FIELD_TO_STEP[e.path] : undefined))
                        .filter(s => typeof s === 'number');
                    if (stepsWithErrors.length) {
                        setActiveStep(Math.min(...stepsWithErrors));
                    }
                    enqueueSnackbar('Revise os campos obrigatórios antes de enviar.', { variant: 'warning' });
                } else if (validationError?.path) {
                    setFbFieldError(validationError.path, validationError.message);
                    const step = FIELD_TO_STEP[validationError.path];
                    if (typeof step === 'number') setActiveStep(step);
                    enqueueSnackbar(validationError.message, { variant: 'warning' });
                } else {
                    enqueueSnackbar(validationError?.message || 'Dados inválidos.', { variant: 'error' });
                }
                return;
            }

            try {
                setSubmitting(true);
                const payload = {
                    name: formValues.name.trim(),
                    businessName: formValues.businessName.trim(),
                    document: formValues.document.trim(),
                    phone: formValues.phone.trim(),
                    description: formValues.description.trim(),
                    zipcode: formValues.zipcode,
                    street: formValues.street.trim(),
                    number: String(formValues.number).trim(),
                    complement: formValues.complement.trim() || undefined,
                    neighborhood: formValues.neighborhood.trim(),
                    city: formValues.city.trim(),
                    state: formValues.state.trim().toUpperCase(),
                    country: 'BR',
                    latitude: formValues.latitude?.toString().trim() || undefined,
                    longitude: formValues.longitude?.toString().trim() || undefined,
                    loginEmail: formValues.loginEmail.trim(),
                    password: formValues.password,
                    existingAccount: useExisting,
                    clubId: clubIdFromUrl || undefined,
                    clubCode: clubIdFromUrl ? undefined : formValues.clubCode?.trim() || undefined,
                };
                if (!useExisting) {
                    payload.confirmPassword = formValues.confirmPassword;
                }
                const { data } = await server.post('advertiser/public/register', payload);
                enqueueSnackbar(data?.message || 'Cadastro realizado.', { variant: 'success' });
                navigate(ADVERTISER_LOGIN_PATH, { replace: true });
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        if (clubCodeFromUrl && values.clubCode !== clubCodeFromUrl) {
            setFieldValue('clubCode', clubCodeFromUrl);
        }
    }, [clubCodeFromUrl, setFieldValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const clearStepFieldErrors = stepIdx => {
        stepFieldKeys[stepIdx]?.forEach(field => setFieldError(field, undefined));
    };

    const handleAccessModeChange = (_event, nextMode) => {
        if (!nextMode || nextMode === accessMode) return;
        clearStepFieldErrors(2);
        setFieldValue('password', '');
        setFieldValue('confirmPassword', '');
        setFieldValue('showPassword', false);
        setFieldValue('showConfirmPassword', false);
        setFieldValue('existingAccount', nextMode === 'existing');
        setAccessMode(nextMode);
    };

    const handleContinueStep = async () => {
        clearStepFieldErrors(activeStep);
        try {
            await stepSchemas[activeStep].validate(values, { abortEarly: false });
            setActiveStep(s => Math.min(STEPS.length - 1, s + 1));
        } catch (validationError) {
            if (validationError?.inner?.length) {
                validationError.inner.forEach(err => {
                    if (err.path) setFieldError(err.path, err.message);
                });
            } else if (validationError?.path) {
                setFieldError(validationError.path, validationError.message);
            }
            enqueueSnackbar('Preencha todos os campos obrigatórios desta etapa.', { variant: 'error' });
        }
    };

    /** Só permite submit real no último passo (senão Enter dispara validate completa e mensagem incorreta) */
    const handleFormWrappedSubmit = e => {
        e.preventDefault();
        if (activeStep !== STEPS.length - 1) return;
        handleSubmit(e);
    };

    const overlayGradient = `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.88)} 0%, ${alpha('#0f172a', 0.82)} 55%, ${alpha('#020617', 0.9)} 100%)`;

    const heroPanel = (
        <Box
            sx={{
                position: 'relative',
                order: { xs: 1, md: 2 },
                flex: { xs: 'none', md: '0 0 50%' },
                width: { xs: '100%', md: '50%' },
                minHeight: { xs: 200, sm: 240, md: '100vh' },
                backgroundImage: `${overlayGradient}, url(${HERO_IMAGE})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: { xs: 3, md: 5 },
                color: 'common.white',
            }}
        >
            <Typography
                variant={isMdUp ? 'h3' : 'h5'}
                sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                    maxWidth: 420,
                }}
            >
                Seu negócio no clube de benefícios
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    mt: 2,
                    maxWidth: 400,
                    opacity: 0.95,
                    lineHeight: 1.6,
                    fontWeight: 500,
                    textShadow: '0 1px 12px rgba(0,0,0,0.25)',
                }}
            >
                Cadastre-se, publique ofertas e valide vouchers em um só lugar — rápido e organizado.
            </Typography>
        </Box>
    );

    //abrir o google maps ja no bairro do individo 
    const handleOpenMaps = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    window.open(
                        `https://www.google.com/maps/@${coords.latitude},${coords.longitude},16z`,
                        '_blank'
                    );
                },
                () => {
                    // fallback se o usuário negar permissão
                    window.open('https://www.google.com/maps', '_blank');
                }
            );
        } else {
            window.open('https://www.google.com/maps', '_blank');
        }
    };

//essa funcao é pq quando ele copia direto do google maps o valor vem junto  -16.695585751801758, -49.29510746636618. Essa funcao separa nos dois inputs
    const handlePasteCoords = (e) => {
        const text = e.clipboardData.getData('text');
        const parts = text.split(',').map(s => s.trim());

        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            setFieldValue('latitude', parts[0]);
            setFieldValue('longitude', parts[1]);
            e.preventDefault();
        }
    };
    return (
        <Stack direction={{ xs: 'column', md: 'row' }} minHeight={{ md: '100vh' }} sx={{ bgcolor: 'background.default' }}>
            <Box
                sx={{
                    order: { xs: 2, md: 1 },
                    flex: { xs: '1 1 auto', md: '0 0 50%' },
                    width: { xs: '100%', md: '50%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    py: { xs: 3, md: 4 },
                    px: { xs: 2, sm: 4, md: 6 },
                    overflowY: 'auto',
                }}
            >
                <Stack spacing={3} component="form" onSubmit={handleFormWrappedSubmit} noValidate sx={{ maxWidth: 680, width: '100%', mx: 'auto' }}>
                    <Box component="img" src={Logo} alt="Eurobem" sx={{ width: 140, alignSelf: { xs: 'center', md: 'flex-start' } }} />

                    <Stack spacing={0.5}>
                        <Typography variant="h5" fontWeight={800}>
                            Cadastro de anunciante
                        </Typography>
                        {hasClubReferralFromUrl ? (
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                Indicação do clube carregada pelo link.
                            </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ pt: 0.5 }}>
                            Já tem conta?{' '}
                            <MuiLink
                                href={ADVERTISER_LOGIN_PATH}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontWeight: 700 }}
                            >
                                Entrar
                            </MuiLink>
                        </Typography>
                    </Stack>

                    <Card variant="outlined" sx={{ width: '100%', borderColor: 'divider' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                            <Stack spacing={2.5}>
                                <Typography variant="body2" color="text.secondary">
                                    {STEPS[activeStep].description}
                                </Typography>

                                <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', py: 0.5 }}>
                                    {STEPS.map(s => (
                                        <Step key={s.label}>
                                            <StepLabel
                                                sx={{
                                                    '& .MuiStepLabel-label': { fontSize: '0.75rem', fontWeight: 600 },
                                                    '& .MuiStepLabel-label.Mui-active': { fontWeight: 700 },
                                                    '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600 },
                                                }}
                                            >
                                                {s.label}
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>

                                {activeStep === 0 && (
                                    <Stack spacing={2}>
                                        {clubIdFromUrl ? (
                                            <TextField
                                                fullWidth
                                                name="clubId"
                                                label="Clube indicador"
                                                value={clubIdFromUrl}
                                                InputProps={{ readOnly: true }}
                                                helperText="Preenchido automaticamente pelo link de indicação."
                                            />
                                        ) : (
                                            <TextField
                                                fullWidth
                                                name="clubCode"
                                                label="Código do clube indicador"
                                                value={values.clubCode}
                                                onChange={handleChange}
                                                InputProps={{
                                                    readOnly: Boolean(clubCodeFromUrl),
                                                }}
                                                helperText={
                                                    clubCodeFromUrl
                                                        ? 'Preenchido automaticamente pelo link de indicação.'
                                                        : 'Opcional. Informe se um clube indicou o seu cadastro.'
                                                }
                                            />
                                        )}
                                        <TextField
                                            required
                                            fullWidth
                                            name="name"
                                            label="Nome do anunciante"
                                            value={values.name}
                                            onChange={handleChange}
                                            error={Boolean(errors.name)}
                                            helperText={errors.name}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="businessName"
                                            label="Razão social"
                                            value={values.businessName}
                                            onChange={handleChange}
                                            error={Boolean(errors.businessName)}
                                            helperText={errors.businessName}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="document"
                                            label="Documento (CNPJ / CPF)"
                                            value={values.document}
                                            onChange={e => setFieldValue('document', e.target?.value || e)}
                                            InputProps={{ inputComponent: DocumentInputMasked }}
                                            error={Boolean(errors.document)}
                                            helperText={errors.document}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="phone"
                                            label="Telefone"
                                            value={values.phone || ''}
                                            onChange={e => setFieldValue('phone', e.target?.value || e)}
                                            InputProps={{ inputComponent: PhoneInputMasked }}
                                            error={Boolean(errors.phone)}
                                            helperText={errors.phone}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="description"
                                            label="Sobre a loja"
                                            value={values.description}
                                            onChange={handleChange}
                                            multiline
                                            minRows={4}
                                            error={Boolean(errors.description)}
                                            helperText={errors.description}
                                        />
                                    </Stack>
                                )}

                                {activeStep === 1 && (
                                    <Stack spacing={2}>
                                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                                            Localização comercial
                                        </Typography>
                                        <Address
                                            onChange={handleChange}
                                            setFieldValue={setFieldValue}
                                            name="zipcode"
                                            setFieldError={setFieldError}
                                            value={values.zipcode}
                                            address={values.address}
                                            error={Boolean(errors.zipcode)}
                                            helperText={errors.zipcode}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            Se o CEP não for localizado ou os dados vierem incompletos, preencha abaixo.
                                        </Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="street"
                                            label="Logradouro (rua, avenida…)"
                                            value={values.street}
                                            onChange={handleChange}
                                            error={Boolean(errors.street)}
                                            helperText={errors.street}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="neighborhood"
                                            label="Bairro"
                                            value={values.neighborhood}
                                            onChange={handleChange}
                                            error={Boolean(errors.neighborhood)}
                                            helperText={errors.neighborhood}
                                        />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField
                                                required
                                                fullWidth
                                                name="city"
                                                label="Cidade"
                                                value={values.city}
                                                onChange={handleChange}
                                                error={Boolean(errors.city)}
                                                helperText={errors.city}
                                            />
                                            <TextField
                                                required
                                                name="state"
                                                label="UF"
                                                value={values.state}
                                                onChange={e =>
                                                    setFieldValue(
                                                        'state',
                                                        e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2),
                                                    )
                                                }
                                                sx={{ width: { xs: '100%', sm: 96 } }}
                                                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                                                error={Boolean(errors.state)}
                                                helperText={errors.state}
                                            />
                                        </Stack>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField
                                                required
                                                fullWidth
                                                name="number"
                                                label="Número"
                                                value={values.number}
                                                onChange={handleChange}
                                                error={Boolean(errors.number)}
                                                helperText={errors.number}
                                            />
                                            <TextField
                                                fullWidth
                                                name="complement"
                                                label="Complemento"
                                                value={values.complement}
                                                onChange={handleChange}
                                                error={Boolean(errors.complement)}
                                                helperText={errors.complement}
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                            Para a loja aparecer nas recomendações por proximidade, informar latitude e longitude ajuda bastante —
                                            esse passo não é obrigatório.
                                        </Typography>
                                        {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField
                                                fullWidth
                                                name="latitude"
                                                label="Latitude"
                                                value={values.latitude}
                                                onChange={handleChange}
                                                error={Boolean(errors.latitude)}
                                                helperText={errors.latitude}
                                            />
                                            <TextField
                                                fullWidth
                                                name="longitude"
                                                label="Longitude"
                                                value={values.longitude}
                                                onChange={handleChange}
                                                error={Boolean(errors.longitude)}
                                                helperText={errors.longitude}
                                            />
                                        </Stack> */}

                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField
                                                fullWidth
                                                name="latitude"
                                                label="Latitude"
                                                value={values.latitude}
                                                onChange={handleChange}
                                                onPaste={handlePasteCoords}
                                                error={Boolean(errors.latitude)}
                                                helperText={errors.latitude || 'Cole as coordenadas aqui para preencher os dois campos'}
                                            />
                                            <TextField
                                                fullWidth
                                                name="longitude"
                                                label="Longitude"
                                                value={values.longitude}
                                                onChange={handleChange}
                                                error={Boolean(errors.longitude)}
                                                helperText={errors.longitude}
                                            />
                                        </Stack>
                                        <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<HelpOutlineIcon fontSize="small" />}
                                            onClick={() => setShowTip(v => !v)}
                                            sx={{ textTransform: 'none', px: 0, mt: 0.5, color: 'text.secondary' }}
                                        >
                                            Como encontrar minha localização?
                                        </Button>
                                    </Stack>
                                )}
                                {showTip && (
                                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'background.default' }}>
                                        <Typography variant="caption" component="ol" sx={{ m: 0, pl: 2, lineHeight: 2 }}>
                                            <li>
                                                Acesse o{' '}
                                                <Link
                                                    component="button"
                                                    variant="caption"
                                                    onClick={handleOpenMaps}
                                                >
                                                    Google Maps
                                                </Link>
                                            </li>
                                            <li>Clique com o botão direito no local da sua loja</li>
                                            <li>Clique nos dois números que aparecem, eles são latitude e longitude — ex: <code>-15.7801, -47.9292</code></li>
                                        </Typography>
                                    </Paper>
                                )}

                                {activeStep === 2 && (
                                    <Stack spacing={2}>
                                        <Tabs
                                            value={accessMode}
                                            onChange={handleAccessModeChange}
                                            variant="fullWidth"
                                            sx={{
                                                minHeight: 40,
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 },
                                            }}
                                        >
                                            <Tab value="create" label="Criar conta" />
                                            <Tab value="existing" label="Já tenho conta" />
                                        </Tabs>

                                        {isExistingAccount ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Use o e-mail e a senha da sua conta Eurobem (ex.: acesso de clube) para vincular
                                                este anunciante.
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Crie o e-mail e a senha que serão usados no painel do anunciante.
                                            </Typography>
                                        )}

                                        <TextField
                                            required
                                            fullWidth
                                            name="loginEmail"
                                            type="email"
                                            label="E-mail"
                                            autoComplete="email"
                                            value={values.loginEmail}
                                            onChange={handleChange}
                                            error={Boolean(errors.loginEmail)}
                                            helperText={errors.loginEmail}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            name="password"
                                            type={values.showPassword ? 'text' : 'password'}
                                            label={isExistingAccount ? 'Senha da conta' : 'Senha'}
                                            autoComplete={isExistingAccount ? 'current-password' : 'new-password'}
                                            value={values.password}
                                            onChange={handleChange}
                                            error={Boolean(errors.password)}
                                            helperText={errors.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setFieldValue('showPassword', !values.showPassword)}
                                                            edge="end"
                                                            aria-label="Alternar visibilidade da senha"
                                                        >
                                                            {values.showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {!isExistingAccount ? (
                                            <TextField
                                                required
                                                fullWidth
                                                name="confirmPassword"
                                                type={values.showConfirmPassword ? 'text' : 'password'}
                                                label="Confirmar senha"
                                                autoComplete="new-password"
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                error={Boolean(errors.confirmPassword)}
                                                helperText={errors.confirmPassword}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() =>
                                                                    setFieldValue(
                                                                        'showConfirmPassword',
                                                                        !values.showConfirmPassword,
                                                                    )
                                                                }
                                                                edge="end"
                                                                aria-label="Alternar visibilidade da confirmação de senha"
                                                            >
                                                                {values.showConfirmPassword ? (
                                                                    <VisibilityOff />
                                                                ) : (
                                                                    <Visibility />
                                                                )}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                                <MuiLink
                                                    href={ADVERTISER_RECOVER_PATH}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Esqueci a senha
                                                </MuiLink>
                                                <MuiLink
                                                    href={ADVERTISER_LOGIN_PATH}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Ir para o login
                                                </MuiLink>
                                            </Stack>
                                        )}
                                    </Stack>
                                )}

                                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                                    <Button
                                        type="button"
                                        variant="text"
                                        color="inherit"
                                        disabled={activeStep === 0}
                                        onClick={() => setActiveStep(s => Math.max(0, s - 1))}
                                        sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
                                    >
                                        Voltar
                                    </Button>
                                    <Stack direction="row" spacing={1.5}>
                                        {activeStep < STEPS.length - 1 ? (
                                            <Button type="button" variant="contained" size="large" onClick={handleContinueStep}>
                                                Continuar
                                            </Button>
                                        ) : (
                                            <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                                                Concluir cadastro
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Box>

            {heroPanel}
        </Stack>
    );
}
